/**
 * 業務フロー・ステージの図（F2）のルート定義。業務フローの図・案件から見た業務フロー・
 * ステージの図と、業務マップへ倒す旧ストーリーの URL。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務フローの図・案件から見た業務フロー・ステージの図・旧ストーリーの倒し先のルートを定義する。
 * @graph-connects none
 */
import { createRoute } from '@tanstack/react-router';

import { FlowPage } from '../FlowPage.js';
import { LegacyUserStoryRedirect } from '../LegacyUserStoryRedirect.js';
import { parseFlowSearch } from '../flow-search.js';
import { PjFlowPage } from '../PjFlowPage.js';
import { parsePjSearch, type PjSearch } from '../pj-search.js';
import { FLOW_ROUTE, PJ_FLOW_ROUTE, STAGE_ROUTE, USER_STORY_DETAIL_ROUTE } from '../route-paths.js';
import { StagePage } from '../StagePage.js';
import { rootRoute } from './root.js';

/**
 * ステージの図の検索条件。案件と、左右比較を閉じたこと。
 *
 * 開く操作（`op`）は持たない。ステージの図は節点を URL で開かない。
 *
 * @param raw 生の検索条件
 * @returns 案件と左右比較の開閉
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business ステージの図の検索条件から、案件と左右比較の開閉だけを読む。
 * @graph-connects parsePjSearch [calls] 案件の読み取り
 * @graph-connects parseFlowSearch [calls] 左右比較の開閉の読み取り
 */
export function parseStageSearch(raw: Record<string, unknown>): PjSearch & { compare?: 0 } {
  return { ...parsePjSearch(raw), compare: parseFlowSearch(raw).compare };
}

/** @graph-connects none */
export const flowRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: FLOW_ROUTE,
  component: FlowPage,
  validateSearch: parseFlowSearch,
});
/** @graph-connects none */
export const pjFlowRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: PJ_FLOW_ROUTE,
  component: PjFlowPage,
  validateSearch: parseFlowSearch,
});
/** @graph-connects none */
export const stageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: STAGE_ROUTE,
  component: StagePage,
  validateSearch: parseStageSearch,
});
/** 旧ユーザーストーリー図。業務マップへ倒す（`LegacyUserStoryRedirect`）。 @graph-connects none */
export const legacyUserStoryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: USER_STORY_DETAIL_ROUTE,
  component: LegacyUserStoryRedirect,
});

/** 業務フローのルート。ルート木（`app.tsx`）はこれを並べる。 @graph-connects none */
export const flowRoutes = [flowRoute, pjFlowRoute, stageRoute, legacyUserStoryRoute] as const;
